import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Quote } from "lucide-react";
import { STATS, IMAGES } from "../mock";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 1600;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * value));
      if (progress < 1) requestAnimationFrame(tick);
      else setCount(value);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);

  return (
    <span ref={ref} className="font-display font-extrabold text-4xl md:text-5xl racing-text">
      {count}
      {suffix}
    </span>
  );
};

const About = () => {
  return (
    <section id="about" className="section-pad relative bg-[#0b0b0e] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden red-glow">
              <img
                src={IMAGES.ferrariInterior}
                alt="Intérieur véhicule sportif"
                className="w-full h-[480px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080a]/50 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -right-4 md:right-6 glass rounded-2xl px-6 py-5 border border-[#ff6a00]/30">
              <p className="font-display font-extrabold text-3xl text-white">2025</p>
              <p className="text-white/60 text-xs tracking-wide uppercase">Année de création</p>
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="eyebrow">BS Import Auto</span>
            <h2 className="section-title text-4xl md:text-5xl text-white mt-5">
              La passion de l'automobile, <span className="racing-text">l'expertise de l'import</span>
            </h2>
            <div className="mt-6 space-y-4 text-white/60 leading-relaxed">
              <p>
                BS Import Auto a vu le jour officiellement en février 2025. Depuis
                plusieurs années, j'importe des véhicules en provenance
                d'Allemagne, d'Italie et de Belgique, en maîtrisant chaque étape
                du processus.
              </p>
              <p>
                J'ai fait le choix de mettre cette expertise au service des
                autres : une solution fiable, rapide et efficace, pensée pour
                s'adapter à tous les budgets et à tous les profils. Aujourd'hui,
                je comptabilise plus de <span className="text-white font-semibold">40 clients satisfaits</span>.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {STATS.map((stat) => (
                <div key={stat.label}>
                  <Counter value={stat.value} suffix={stat.suffix} />
                  <p className="mt-1 text-white/50 text-xs md:text-sm leading-tight">{stat.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <Quote className="hidden" />
    </section>
  );
};

export default About;
